// Logged-out, token-authenticated account pages — the landing targets for the
// emails auth-service sends. They use authClient (plain fetch) only; no session
// is established here, so they live OUTSIDE the AppProviders/RequireAuth tree.
//   /forgot-password — request a reset link (enumeration-safe)
//   /reset-password?token= — set a new password
//   /verify-email?token=   — confirm an email-change
// Visual language follows Login.tsx: console tokens, the forms/ui primitives,
// one lime action per view, 44 px targets on phones, lime focus rings.

import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { ArrowLeft, Check, KeyRound, Loader2, Lock, Mail, MailCheck } from "lucide-react";

import { PowerBESSLogo } from "@/components/PowerBESSLogo";
import { ActionButton, ErrorPanel } from "@/app/console/ui";
import { Field, Input } from "@/app/console/forms";
import { authClient } from "@/lib/auth/authClient";
import type { ApiError } from "@/lib/api/errors";

function errMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) return String((e as ApiError).message);
  return "Something went wrong. Please try again.";
}

// ---------------------------------------------------------------------------
// frame — the Login form column, centred; boxed on surface-1 from `sm` up so
// phones get exactly the Login phone layout (unboxed on surface-0).
// ---------------------------------------------------------------------------

function Shell({ title, subtitle, icon: Icon, children }: { title: string; subtitle?: ReactNode; icon: LucideIcon; children: ReactNode }) {
  // Browser-tab title: the three recovery flows are "Reset password" except the
  // e-mail verification landing.
  useEffect(() => {
    document.title = `${/verif/i.test(title) ? "Verify email" : "Reset password"} · PowerBESS`;
  }, [title]);
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-surface-0 px-5 py-10 text-text-1">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: "linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at 50% 45%, black 5%, transparent 62%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 45%, black 5%, transparent 62%)",
        }}
      />

      <main className="relative w-full max-w-[400px] sm:max-w-[464px]">
        <a href="https://powerbess.ai" className="mb-8 flex w-fit items-center gap-2.5">
          <PowerBESSLogo size={28} />
          <span className="text-lg font-semibold">Power<span className="text-brand">BESS</span>.ai</span>
        </a>

        <section className="sm:rounded-card sm:border sm:border-line sm:bg-surface-1 sm:p-8">
          <span className="mb-6 flex h-11 w-11 items-center justify-center rounded-card border border-line bg-surface-2 text-text-1">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight 2xl:text-3xl">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-text-2 2xl:text-base">{subtitle}</p>}
          <div className="mt-8 space-y-5">{children}</div>
        </section>

        <div className="mt-10 flex items-center justify-center gap-4 text-xs text-text-3 2xl:text-sm">
          <a href="https://powerbess.ai" className="inline-flex h-11 items-center hover:text-text-1 sm:h-auto">powerbess.ai</a>
          <span aria-hidden="true">·</span>
          <Link to="/privacy" className="inline-flex h-11 items-center hover:text-text-1 sm:h-auto">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" className="inline-flex h-11 items-center hover:text-text-1 sm:h-auto">Terms</Link>
        </div>
      </main>
    </div>
  );
}

/** Informational / success note — the ok-toned twin of `ErrorPanel`. */
function Notice({ tone, icon: Icon, children }: { tone: "ok" | "neutral"; icon: LucideIcon; children: ReactNode }) {
  const cls = tone === "ok" ? "border-ok/30 bg-ok/[0.06] text-ok" : "border-line bg-surface-2 text-text-2";
  return (
    <div role="status" className={`flex items-start gap-3 rounded-card border p-3 text-sm ${cls}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function SignInLink() {
  return (
    <Link
      to="/login"
      className="mx-auto flex h-11 w-fit items-center gap-1.5 px-3 text-xs text-text-3 transition-colors duration-fast hover:text-text-1 sm:h-9"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back to sign in
    </Link>
  );
}

// System control heights: 44 px on phones, 36 px from `sm` up (the same
// `h-11 sm:h-9` pattern ActionButton size="md" uses). The responsive variant is
// what lets this win over Input's base `h-9`.
const INPUT_H = "max-sm:h-11";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      // Enumeration-safe: always resolves ok regardless of whether the email exists.
      await authClient.forgotPassword(email.trim());
      setSent(true);
    } catch {
      // Backend never reveals existence; treat any non-network outcome as "sent".
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Reset your password" icon={KeyRound} subtitle={sent ? undefined : "Enter your email and we’ll send you a reset link."}>
      {sent ? (
        <Notice tone="neutral" icon={MailCheck}>
          If an account exists for <span className="text-text-1">{email.trim()}</span>, we’ve sent a password-reset link. It expires in 30 minutes.
        </Notice>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <Field label="Work email" htmlFor="email">
            <Input
              id="email"
              type="email"
              icon={Mail}
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={INPUT_H}
            />
          </Field>
          <ActionButton type="submit" loading={busy} disabled={busy || !email} className="w-full">
            Send reset link
          </ActionButton>
        </form>
      )}
      <SignInLink />
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mismatch = confirm.length > 0 && pw !== confirm;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pw !== confirm) return;
    setBusy(true);
    setErr(null);
    try {
      await authClient.resetPassword(token, pw);
      setDone(true);
    } catch (er) {
      setErr(errMessage(er));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <Shell title="Reset password" icon={Lock}>
        <ErrorPanel message="This reset link is invalid or incomplete." />
        <SignInLink />
      </Shell>
    );
  }

  return (
    <Shell title="Set a new password" icon={Lock} subtitle={done ? undefined : "Choose a new password for your account."}>
      {done ? (
        <Notice tone="ok" icon={Check}>
          Your password has been reset. You can now sign in.
        </Notice>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <Field label="New password" htmlFor="new-password" hint="At least 8 characters.">
            <Input
              id="new-password"
              type="password"
              icon={Lock}
              required
              minLength={8}
              autoComplete="new-password"
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              className={INPUT_H}
            />
          </Field>
          <Field label="Confirm new password" htmlFor="confirm-password" error={mismatch ? "Passwords don’t match." : undefined}>
            <Input
              id="confirm-password"
              type="password"
              icon={Lock}
              required
              minLength={8}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              invalid={mismatch}
              className={INPUT_H}
            />
          </Field>
          {err && <ErrorPanel message={err} />}
          <ActionButton type="submit" loading={busy} disabled={busy || pw.length < 8 || mismatch} className="w-full">
            Reset password
          </ActionButton>
        </form>
      )}
      <SignInLink />
    </Shell>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMsg("This verification link is invalid or incomplete.");
      return;
    }
    let cancelled = false;
    authClient
      .verifyEmail(token)
      .then(() => !cancelled && setState("ok"))
      .catch((e) => {
        if (!cancelled) {
          setState("error");
          setMsg(errMessage(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Shell title="Email verification" icon={MailCheck}>
      {state === "loading" && (
        <div role="status" className="flex items-center gap-2 text-sm text-text-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Confirming your email…
        </div>
      )}
      {state === "ok" && (
        <Notice tone="ok" icon={Check}>
          Your email address has been confirmed.
        </Notice>
      )}
      {state === "error" && <ErrorPanel message={msg} />}
      <SignInLink />
    </Shell>
  );
}
